import type { Notification } from '@/types/notification';
import { router } from 'expo-router';
import React, { type ReactNode } from 'react';
import { Pressable, Text, View } from 'react-native';
import type { NotificationType } from './notification-types';
import { isCheckInComment, isCheckInLike, isFriendRecommendation } from './notification-types';

interface NotificationContentProps {
  notification: Notification;
  onUserPress: () => void;
}

export function NotificationContent({ notification, onUserPress }: NotificationContentProps) {
  return (
    <View className="flex-1 mr-2">
      {notification.user && (
        <Pressable onPress={onUserPress}>
          <Text className="font-bold text-foreground">
            {notification.user.name}
          </Text>
        </Pressable>
      )}
      {renderContentForType(notification)}
      {notification.restaurant && (
        <Text className="font-medium mt-1 text-muted-foreground">
          {notification.restaurant}
          {notification.dish && ` - ${notification.dish}`}
          {notification.distance && ` - ${notification.distance}`}
        </Text>
      )}
    </View>
  );
}

const CONTENT_RENDERERS: Partial<Record<NotificationType, (n: Notification) => ReactNode>> = {
  like: (n) => (isCheckInLike(n) ? <LikedCheckInContent notification={n} /> : null),
  comment: (n) => (isCheckInComment(n) ? <CommentCheckInContent notification={n} /> : null),
  friend_recommendation: (n) => (isFriendRecommendation(n) ? <FriendRecommendationContent notification={n} /> : null),
};

function renderContentForType(notification: Notification): ReactNode {
  const renderer = CONTENT_RENDERERS[notification.type];
  const custom = renderer?.(notification);

  if (custom) return custom;

  return (
    <Text className="text-muted-foreground">
      {notification.content}
    </Text>
  );
}

function LikedCheckInContent({ notification }: { notification: Notification }) {
  return (
    <Text className="text-muted-foreground">
      liked your check-in for{' '}
      <Text
        className="text-white font-bold"
        onPress={() =>
          router.push({
            pathname: '/(root)/menu-item/[id]',
            params: { id: notification.menuItemId! },
          })
        }
      >
        {notification.menuItemName}
      </Text>
      {' '}at{' '}
      <Text
        className="text-white font-bold"
        onPress={() =>
          router.push({
            pathname: '/(root)/business/[id]',
            params: { id: notification.businessId! },
          })
        }
      >
        {notification.businessName}
      </Text>
    </Text>
  );
}

function CommentCheckInContent({ notification }: { notification: Notification }) {
  return (
    <Text className="text-muted-foreground">
      commented on your check-in for{' '}
      <Text
        className="text-primary font-bold"
        onPress={() =>
          router.push({
            pathname: '/(root)/menu-item/[id]',
            params: { id: notification.menuItemId! },
          })
        }
      >
        {notification.menuItemName}
      </Text>
      {' '}at{' '}
      <Text
        className="text-primary font-bold"
        onPress={() =>
          router.push({
            pathname: '/(root)/business/[id]',
            params: { id: notification.businessId! },
          })
        }
      >
        {notification.businessName}
      </Text>
    </Text>
  );
}

function FriendRecommendationContent({ notification }: { notification: Notification }) {
  return (
    <Text className="text-muted-foreground">
      recommended{' '}
      <Text
        className="text-white font-bold"
        onPress={() =>
          router.push({
            pathname: '/(root)/menu-item/[id]',
            params: { id: notification.menuItemId! },
          })
        }
      >
        {notification.menuItemName}
      </Text>
      {' '}at{' '}
      <Text
        className="text-white font-bold"
        onPress={() =>
          router.push({
            pathname: '/(root)/business/[id]',
            params: { id: notification.businessId! },
          })
        }
      >
        {notification.businessName}
      </Text>
      {notification.content && !notification.content.startsWith('recommended') && (
        <>
          {'\n'}
          <Text className="text-muted-foreground/70 text-xs">
            &quot;{notification.content}&quot;
          </Text>
        </>
      )}
    </Text>
  );
}
